// redux pattern
/* eslint-disable no-shadow */
import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { FiX } from 'react-icons/fi';

import classNames from 'classnames';
import {
   filter,
   fromEvent,
   map,
   merge,
   switchMap,
   take,
   takeUntil,
   tap,
} from 'rxjs';

import {
   setView,
   pianoRollNoteMousedown,
   pianoRollMousemove,
   pianoRollMousedown,
   pianoRollNoteMouseenter,
   pianoRollNoteResizeHandleMousedown,
   clickNoteDeleteIcon,
   pianoRollMouseup,
} from '../actions';
import { sequencerViewTypes } from '../constants';
import { PianoRollKeyboard } from './PianoRollKeyboard';
import { PianoRollGrid } from './PianoRollGrid';
import { PianoRollNotes } from './PianoRollNotes';
import { PianoRollPendingNote } from './PianoRollPendingNote';
import { SequencerProgress } from '../SequencerProgress';
import { PianoRollCursor } from './PianoRollCursor';
import { Button } from '../../../components/Button';

const mapStateToProps = ({
   activeInterface: {
      instrumentRack,
      view,
      pianoRoll,
      noteRange,
      beatCount,
      partsPerBeat,
   },
}) => ({
   instrumentRack,
   view,
   pianoRoll,
   noteRange,
   beatCount,
   partsPerBeat,
});

const mapDispatchToProps = {
   setView,
   pianoRollNoteMousedown,
   pianoRollMousemove,
   pianoRollMousedown,
   pianoRollMouseup,
   pianoRollNoteMouseenter,
   pianoRollNoteResizeHandleMousedown,
   clickNoteDeleteIcon,
};

const PianoRoll = connect(
   mapStateToProps,
   mapDispatchToProps
)(
   ({
      instrumentRack,
      view,
      pianoRoll,
      beatCount,
      partsPerBeat,
      compareAnswer,
      setView,
      pianoRollNoteMousedown,
      pianoRollMouseup,
      pianoRollMousemove,
      pianoRollMousedown,
      pianoRollNoteMouseenter,
      pianoRollNoteResizeHandleMousedown,
      clickNoteDeleteIcon,
   }) => {
      const currentInstrument = instrumentRack[view.instrumentIndex];
      const [lowNote, highNote] = view.noteRange;
      const [touchEnabled, setTouchEnabled] = useState(false);
      const partWidth = pianoRoll.beatWidth / partsPerBeat;

      const pianoRollRef = useRef();

      useEffect(() => {
         const touchstart$ = fromEvent(pianoRollRef.current, 'touchstart')
            .pipe(
               tap(() => setTouchEnabled(true)),
               take(1)
            )
            .subscribe();

         return () => touchstart$.unsubscribe();
      }, []);

      useEffect(() => {
         const createMoveObservable = ({ target }) =>
            fromEvent(target, 'touchmove').pipe(
               tap((e) => {
                  e.preventDefault();
                  pianoRollMousemove(
                     e.touches[0].clientX,
                     e.touches[0].clientY
                  );
               }),
               takeUntil(
                  merge(
                     fromEvent(target, 'touchend'),
                     fromEvent(target, 'touchcancel')
                  ).pipe(
                     tap((e) => {
                        e.preventDefault();
                        pianoRollMouseup(0);
                     }),
                     take(1)
                  )
               )
            );

         const subscription = fromEvent(pianoRollRef.current, 'touchstart')
            .pipe(
               filter(
                  ({ target }) =>
                     target?.dataset?.noteResize || target?.dataset?.noteBody
               ),
               tap((e) => {
                  const { clientX, clientY } = e.touches[0];
                  e.preventDefault();
                  if (e.target.dataset.noteResize) {
                     pianoRollNoteResizeHandleMousedown(
                        Number(e.target.dataset.startPart),
                        Number(e.target.dataset.note),
                        Number(e.target.dataset.noteLength),
                        0,
                        clientX,
                        clientY
                     );
                  } else if (e.target.dataset.noteBody) {
                     pianoRollNoteMousedown(
                        Number(e.target.dataset.startPart),
                        Number(e.target.dataset.note),
                        0,
                        clientX,
                        clientY
                     );
                  }
               }),
               switchMap((e) => createMoveObservable(e))
            )
            .subscribe();

         return () => subscription.unsubscribe();
      }, [
         pianoRollMousemove,
         pianoRollMouseup,
         pianoRollNoteResizeHandleMousedown,
         pianoRollNoteMousedown,
      ]);

      return (
         <div
            ref={pianoRollRef}
            className={classNames('piano-roll', {
               'piano-roll-touch': touchEnabled,
            })}
            onContextMenu={(e) => e.preventDefault()}
         >
            <SequencerProgress />
            <div className="piano-roll-wrapper">
               <PianoRollKeyboard
                  lowNote={lowNote}
                  highNote={highNote}
                  keyHeight={pianoRoll.noteHeight}
                  keyWidth={75}
               />

               <div className="piano-roll-notes-wrapper">
                  <PianoRollGrid
                     lowNote={lowNote}
                     highNote={highNote}
                     noteHeight={pianoRoll.noteHeight}
                     beatCount={beatCount}
                     partsPerBeat={partsPerBeat}
                     partWidth={partWidth}
                     snap={pianoRoll.partSnap}
                  />
                  <PianoRollNotes
                     pattern={currentInstrument.pattern}
                     referencePattern={currentInstrument.referencePattern}
                     compareAnswer={compareAnswer}
                     lowNote={lowNote}
                     highNote={highNote}
                     noteHeight={pianoRoll.noteHeight}
                     beatCount={beatCount}
                     partsPerBeat={partsPerBeat}
                     partWidth={partWidth}
                     onMouseDown={pianoRollMousedown}
                     onNoteMousedown={pianoRollNoteMousedown}
                     onNoteMouseenter={pianoRollNoteMouseenter}
                     onNoteResizeHandleMousedown={
                        pianoRollNoteResizeHandleMousedown
                     }
                     cachedNoteSettings={pianoRoll.cachedNoteSettings}
                     touchEnabled={touchEnabled}
                     clickNoteDeleteIcon={clickNoteDeleteIcon}
                  />
                  {pianoRoll.pendingNote && (
                     <PianoRollPendingNote
                        lowNote={lowNote}
                        highNote={highNote}
                        noteHeight={pianoRoll.noteHeight}
                        beatCount={beatCount}
                        partsPerBeat={partsPerBeat}
                        partWidth={partWidth}
                        pendingNote={pianoRoll.pendingNote}
                        onMousemove={pianoRollMousemove}
                     />
                  )}
                  <PianoRollCursor
                     lowNote={lowNote}
                     highNote={highNote}
                     noteHeight={pianoRoll.noteHeight}
                     partsPerBeat={partsPerBeat}
                     partWidth={partWidth}
                  />
               </div>
            </div>
            {!view.hideCloseButton && (
               <div className="piano-roll-close">
                  <Button
                     className="button-icon"
                     type="button"
                     onClick={() =>
                        setView({
                           viewType: sequencerViewTypes.stepSequencer,
                        })
                     }
                  >
                     <FiX />
                  </Button>
               </div>
            )}
         </div>
      );
   }
);

export { PianoRoll };
